import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Edit2, Trash2, Clock, Lock, Printer, FileDown, TrendingUp, TrendingDown, Minus, Trophy, CalendarDays, CalendarRange } from 'lucide-react';
import DataManager from '../components/DataManager';
import CurrencyDisplay, { CurrencyLabel } from '../components/CurrencyDisplay';
import PrintReportModal from '../components/PrintReportModal';
import UAEClock, { getUAEDateString, getUAETimeString, formatUAEDateDDMMYYYY, toUAEDateString, getUAEDayName, getUAETimeHHMM } from '../components/UAEClock';

// فترة السماح للتعديل للحلاق (ساعة واحدة)
const EDIT_WINDOW_MINUTES = 60;

const WEEKDAYS_AR = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];

// قائمة آخر 12 شهرًا للفلترة (الشهر الحالي افتراضًا)
const buildMonthOptions = () => {
  const today = new Date();
  const opts = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    opts.push({
      value: `${year}-${month}`,
      label: d.toLocaleDateString('ar-AE', { year: 'numeric', month: 'long' }),
    });
  }
  return opts;
};

// أول/آخر يوم بصيغة YYYY-MM-DD لشهر معطى (YYYY-MM)
const getMonthRange = (yyyyMm) => {
  const [y, m] = yyyyMm.split('-').map(Number);
  const start = `${y}-${String(m).padStart(2, '0')}-01`;
  // آخر يوم في الشهر
  const last = new Date(y, m, 0).getDate();
  const end = `${y}-${String(m).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
  return { start, end };
};

// شهر الإيراد (YYYY-MM) محسوب بتوقيت الإمارات
const revenueUAEMonth = (isoDate) => {
  const ymd = toUAEDateString(isoDate);
  return ymd ? ymd.slice(0, 7) : '';
};

// بطاقة "أفضل أداء" — يوم/أسبوع/يوم الأسبوع
const ACCENTS = {
  amber:   { ring: 'border-amber-500/30',   icon: 'text-amber-500',   bg: 'from-amber-500/10', value: 'text-amber-400' },
  emerald: { ring: 'border-emerald-500/30', icon: 'text-emerald-500', bg: 'from-emerald-500/10', value: 'text-emerald-400' },
  blue:    { ring: 'border-blue-500/30',    icon: 'text-blue-500',    bg: 'from-blue-500/10', value: 'text-blue-400' },
};
const PeakCard = ({ icon: Icon, accent, title, primary, subtitle, amount, clients, empty, testId, onClick, isActive = false }) => {
  const a = ACCENTS[accent] || ACCENTS.amber;
  const hasData = !!primary;
  const clickable = hasData && typeof onClick === 'function';
  return (
    <div
      className={`bg-card border ${a.ring} rounded-xl shadow-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent ${clickable ? 'cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all' : ''} ${isActive ? 'ring-2 ring-offset-2 ring-offset-background ' + a.icon.replace('text-', 'ring-') : ''}`}
      data-testid={testId}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-muted-foreground font-semibold">{title}</p>
        <Icon className={`w-4 h-4 ${a.icon}`} />
      </div>
      {hasData ? (
        <>
          <p className={`text-xl font-bold ${a.value}`} data-testid={`${testId}-label`}>{primary}</p>
          {subtitle && <p className="text-[10px] text-muted-foreground mt-1">{subtitle}</p>}
          <div className="flex items-center gap-3 mt-3 text-xs">
            <div data-testid={`${testId}-amount`}>
              <span className="text-muted-foreground">إيراد: </span>
              <span className="font-bold"><CurrencyDisplay amount={amount || 0} /></span>
            </div>
            <div className="w-px h-3 bg-white/10" />
            <div data-testid={`${testId}-clients`}>
              <span className="text-muted-foreground">زبائن: </span>
              <span className="font-bold">{clients || 0}</span>
            </div>
          </div>
          {clickable && (
            <p className="text-[10px] text-muted-foreground mt-2 italic">
              {isActive ? '✓ مفلتر — اضغط مرة أخرى للإلغاء' : 'اضغط لتفلتر الجدول'}
            </p>
          )}
        </>
      ) : (
        <p className="text-sm text-muted-foreground italic">{empty}</p>
      )}
    </div>
  );
};

// شارة المقارنة السنوية: تعرض النسبة المئوية مع اتجاه النمو/التراجع
const YoYBadge = ({ delta, prevValue, lastYearMonth, isCurrency = false }) => {
  if (delta.pct === null) {
    return (
      <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] text-muted-foreground bg-secondary/30 px-2 py-1 rounded-md">
        <Minus className="w-3 h-3" />
        لا توجد بيانات للسنة الماضية ({lastYearMonth})
      </div>
    );
  }
  const isUp = delta.direction === 'up';
  const isFlat = delta.direction === 'neutral';
  const color = isFlat ? 'text-muted-foreground bg-secondary/30' : isUp ? 'text-emerald-500 bg-emerald-500/10' : 'text-rose-500 bg-rose-500/10';
  const Icon = isFlat ? Minus : isUp ? TrendingUp : TrendingDown;
  const formatted = isCurrency
    ? `${prevValue.toFixed(0)} د.إ`
    : `${prevValue}`;
  return (
    <div className={`mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-md ${color}`}>
      <Icon className="w-3 h-3" />
      <span>{isUp ? '+' : isFlat ? '' : '−'}{delta.pct.toFixed(1)}%</span>
      <span className="text-[10px] opacity-75 font-normal">vs {lastYearMonth} ({formatted})</span>
    </div>
  );
};

const Revenues = () => {
  const { t } = useTranslation();
  const { isAdmin, isBarber, user } = useAuth();
  const [revenues, setRevenues] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);  // prevents double-submit
  const [editingRevenue, setEditingRevenue] = useState(null);
  const [formData, setFormData] = useState({
    barber_id: '',
    amount: '',
    client_count: '',
    date: getUAEDateString(),
    time: getUAETimeString(),
    description: ''
  });

  // للحلاق: الحصول على barber_id الخاص به
  const [currentBarber, setCurrentBarber] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);

  // فلتر الشهر (افتراضي: الشهر الحالي بتوقيت الإمارات)
  const monthOptions = React.useMemo(() => buildMonthOptions(), []);
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0].value);

  // فلتر فرعي للنقر على بطاقات "أفضل أداء"
  // { type: 'day', value: 'YYYY-MM-DD' } | { type: 'weekday', value: 0-6 } | { type: 'week', value: 1-5 } | null
  const [drillFilter, setDrillFilter] = useState(null);

  // Export CSV - الشهر المختار
  const exportCSV = () => {
    if (monthRevenues.length === 0) {
      toast.error('لا توجد إيرادات في هذا الشهر');
      return;
    }
    // BOM لدعم العربية في Excel
    const BOM = '\uFEFF';
    const headers = ['التاريخ', 'اليوم', 'الوقت', 'الحلاق', 'المبلغ', 'الزبائن', 'طريقة الدفع', 'الوصف'];
    const rows = monthRevenues.map((r) => {
      const ymd = toUAEDateString(r.date) || '';
      const dayName = getUAEDayName(r.date) || '';
      const time = r.receipt_time || getUAETimeHHMM(r.date) || '';
      const barber = barbers.find(b => b.id === r.barber_id);
      const amt = (r.amount || 0).toFixed(2);
      const cli = r.client_count || r.customers || 0;
      const pm = r.payment_method === 'machine' ? 'بطاقة' : 'نقدي';
      const desc = (r.description || r.notes || '').replace(/"/g, '""').replace(/\n/g, ' ');
      const safe = (v) => `"${String(v).replace(/"/g, '""')}"`;
      return [ymd, dayName, time, barber?.name || '', amt, cli, pm, desc].map(safe).join(',');
    });
    const csv = BOM + [headers.map(h => `"${h}"`).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `revenues_${selectedMonth}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success(`تم تصدير ${monthRevenues.length} إيراد بصيغة CSV`);
  };

  // Download PDF Report - يحترم الشهر المختار
  const downloadPDFReport = async () => {
    try {
      const { start: startDate, end: endDate } = getMonthRange(selectedMonth);
      
      const response = await api.get(`/reports/pdf/revenues?start_date=${startDate}&end_date=${endDate}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `revenues_report_${startDate}_to_${endDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('تم تحميل التقرير بنجاح');
    } catch (error) {
      toast.error('فشل تحميل التقرير');
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [revenuesRes, barbersRes] = await Promise.all([
        api.get('/revenues'),
        api.get('/barbers')
      ]);
      setRevenues(revenuesRes.data);
      setBarbers(barbersRes.data);
    } catch (error) {
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // تحديد الحلاق الحالي إذا كان المستخدم حلاق
  useEffect(() => {
    if (isBarber() && barbers.length > 0 && user) {
      // أولاً: استخدم barber_id من user مباشرة إذا كان موجوداً
      if (user.barber_id) {
        const myBarber = barbers.find(b => b.id === user.barber_id);
        if (myBarber) {
          setCurrentBarber(myBarber);
          setFormData(prev => ({ ...prev, barber_id: myBarber.id }));
          return;
        }
      }
      // ثانياً: ابحث عن الحلاق بـ user_id أو email
      const myBarber = barbers.find(b => b.user_id === user.id || b.email === user.email);
      if (myBarber) {
        setCurrentBarber(myBarber);
        setFormData(prev => ({ ...prev, barber_id: myBarber.id }));
      }
    }
  }, [barbers, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      // إذا كان المستخدم حلاق، استخدم barber_id الخاص به
      const barberId = isBarber() && currentBarber ? currentBarber.id : formData.barber_id;
      
      if (!barberId) {
        toast.error('يرجى تحديد الحلاق');
        return;
      }
      
      const payload = {
        ...formData,
        barber_id: barberId,
        amount: parseFloat(formData.amount),
        client_count: parseInt(formData.client_count),
        // Always interpret the entered date/time as UAE local (UTC+04:00) regardless
        // of the admin's browser timezone — prevents off-by-one-day drift.
        date: new Date(`${formData.date}T${formData.time}:00+04:00`).toISOString(),
        receipt_time: formData.time,
        // Idempotency key
        client_request_id: editingRevenue ? undefined : (
          (typeof crypto !== 'undefined' && crypto.randomUUID)
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`
        ),
      };

      if (editingRevenue) {
        await api.put(`/revenues/${editingRevenue.id}`, payload);
        toast.success('تم تحديث الإيراد بنجاح');
      } else {
        await api.post('/revenues', payload);
        toast.success('تم إضافة الإيراد بنجاح');
      }
      
      setOpen(false);
      setEditingRevenue(null);
      setFormData({
        barber_id: isBarber() && currentBarber ? currentBarber.id : '',
        amount: '',
        client_count: '',
        date: getUAEDateString(),
        time: getUAETimeString(),
        description: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشلت العملية');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (revenue) => {
    // التحقق من صلاحية التعديل للحلاق
    if (isBarber() && !canBarberEdit(revenue)) {
      toast.error('انتهت فترة التعديل (ساعة واحدة)');
      return;
    }
    
    setEditingRevenue(revenue);
    setFormData({
      barber_id: revenue.barber_id,
      amount: revenue.amount.toString(),
      client_count: (revenue.client_count || revenue.customers || 1).toString(),
      date: toUAEDateString(revenue.date) || revenue.date.split('T')[0],
      time: revenue.receipt_time || getUAETimeHHMM(revenue.date) || getUAETimeString(),
      description: revenue.description || ''
    });
    setOpen(true);
  };

  // حذف الإيراد (للمدير فقط)
  const handleDelete = async (revenueId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الإيراد؟')) return;
    try {
      await api.delete(`/revenues/${revenueId}`);
      toast.success('تم حذف الإيراد بنجاح');
      fetchData();
    } catch (error) {
      toast.error('فشل حذف الإيراد');
    }
  };

  // التحقق إذا كان الإيراد قابل للتعديل للحلاق
  const canBarberEdit = (revenue) => {
    if (!isBarber()) return false;
    // التحقق من أن الإيراد للحلاق نفسه
    if (currentBarber && revenue.barber_id !== currentBarber.id) return false;
    const createdAt = new Date(revenue.created_at);
    const minutesElapsed = (new Date() - createdAt) / (1000 * 60);
    return minutesElapsed < EDIT_WINDOW_MINUTES;
  };

  // حساب الوقت المتبقي للتعديل
  const getTimeRemaining = (revenue) => {
    const createdAt = new Date(revenue.created_at);
    const minutesElapsed = (new Date() - createdAt) / (1000 * 60);
    const minutesRemaining = EDIT_WINDOW_MINUTES - minutesElapsed;
    if (minutesRemaining <= 0) return null;
    if (minutesRemaining < 1) return 'أقل من دقيقة';
    return `${Math.floor(minutesRemaining)} دقيقة`;
  };

  const canAddOrEdit = isAdmin() || isBarber();

  // إعادة تعيين الـ drill عند تغيير الشهر
  useEffect(() => { setDrillFilter(null); }, [selectedMonth]);

  // إيرادات الشهر فقط (بدون drill) — تستخدم لحساب الإجماليات وبطاقات "أفضل أداء"
  const monthRevenues = React.useMemo(() => {
    return revenues.filter((r) => revenueUAEMonth(r.date) === selectedMonth);
  }, [revenues, selectedMonth]);

  // فلترة الإيرادات حسب الشهر المختار + الـ drill (للجدول فقط)
  const filteredRevenues = React.useMemo(() => {
    if (!drillFilter) return monthRevenues;
    return monthRevenues.filter((r) => {
      const ymd = toUAEDateString(r.date);
      if (!ymd) return false;
      if (drillFilter.type === 'day') return ymd === drillFilter.value;
      const [yy, mm, dd] = ymd.split('-').map(Number);
      if (drillFilter.type === 'weekday') {
        return new Date(Date.UTC(yy, mm - 1, dd)).getUTCDay() === drillFilter.value;
      }
      if (drillFilter.type === 'week') {
        return Math.floor((dd - 1) / 7) + 1 === drillFilter.value;
      }
      return true;
    });
  }, [monthRevenues, drillFilter]);

  // مقارنة سنوية: نفس الشهر من السنة الماضية
  const yoyStats = React.useMemo(() => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const lastYearMonth = `${y - 1}-${String(m).padStart(2, '0')}`;
    const prev = revenues.filter((r) => revenueUAEMonth(r.date) === lastYearMonth);
    const prevRevenue = prev.reduce((s, r) => s + (r.amount || 0), 0);
    const prevClients = prev.reduce((s, r) => s + (r.client_count || r.customers || 0), 0);
    return { lastYearMonth, prevRevenue, prevClients, hasData: prev.length > 0 };
  }, [revenues, selectedMonth]);

  // أفضل يوم/أسبوع/يوم في الأسبوع للشهر المختار (مستقل عن الـ drill)
  const peakStats = React.useMemo(() => {
    if (monthRevenues.length === 0) {
      return { bestDay: null, bestWeekday: null, bestWeek: null };
    }
    // مجموعات
    const byDate = new Map();    // YYYY-MM-DD → {amount, clients}
    const byWeekday = new Map(); // 0..6 → {amount, clients, count}
    const byWeek = new Map();    // 1..5 → {amount, clients, days:Set}

    for (const r of monthRevenues) {
      const ymd = toUAEDateString(r.date); // YYYY-MM-DD بتوقيت الإمارات
      if (!ymd) continue;
      const day = parseInt(ymd.slice(8, 10), 10);
      // weekday: نحوّل الـ ymd إلى Date في UTC ثم نقرأ اليوم. (UTC kf لتجنب جر التايم-زون)
      const [yy, mm, dd] = ymd.split('-').map(Number);
      const weekday = new Date(Date.UTC(yy, mm - 1, dd)).getUTCDay(); // 0=Sunday
      const weekIdx = Math.floor((day - 1) / 7) + 1; // 1..5

      const amt = r.amount || 0;
      const cli = r.client_count || r.customers || 0;

      const d = byDate.get(ymd) || { amount: 0, clients: 0 };
      d.amount += amt; d.clients += cli;
      byDate.set(ymd, d);

      const wd = byWeekday.get(weekday) || { amount: 0, clients: 0, days: new Set() };
      wd.amount += amt; wd.clients += cli; wd.days.add(ymd);
      byWeekday.set(weekday, wd);

      const wk = byWeek.get(weekIdx) || { amount: 0, clients: 0, days: new Set() };
      wk.amount += amt; wk.clients += cli; wk.days.add(ymd);
      byWeek.set(weekIdx, wk);
    }

    const pickMax = (map, getAmt) => {
      let bestKey = null;
      let best = -Infinity;
      for (const [k, v] of map.entries()) {
        const a = getAmt(v);
        if (a > best) { best = a; bestKey = k; }
      }
      return bestKey;
    };

    const bestDateKey = pickMax(byDate, (v) => v.amount);
    const bestWeekdayKey = pickMax(byWeekday, (v) => v.amount);
    const bestWeekKey = pickMax(byWeek, (v) => v.amount);

    const bestDay = bestDateKey ? (() => {
      const [byy, bmm, bdd] = bestDateKey.split('-').map(Number);
      const wd = new Date(Date.UTC(byy, bmm - 1, bdd)).getUTCDay();
      return {
        date: bestDateKey,
        label: `${WEEKDAYS_AR[wd]} ${String(bdd).padStart(2, '0')}/${String(bmm).padStart(2, '0')}`,
        ...byDate.get(bestDateKey),
      };
    })() : null;
    const bestWeekday = bestWeekdayKey !== null ? {
      name: WEEKDAYS_AR[bestWeekdayKey],
      weekdayIdx: bestWeekdayKey,
      amount: byWeekday.get(bestWeekdayKey).amount,
      clients: byWeekday.get(bestWeekdayKey).clients,
      daysCount: byWeekday.get(bestWeekdayKey).days.size,
    } : null;
    const bestWeek = bestWeekKey !== null ? {
      number: bestWeekKey,
      ...byWeek.get(bestWeekKey),
      daysCount: byWeek.get(bestWeekKey).days.size,
    } : null;

    return { bestDay, bestWeekday, bestWeek };
  }, [monthRevenues]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const totalRevenue = filteredRevenues.reduce((sum, r) => sum + r.amount, 0);
  const totalClients = filteredRevenues.reduce((sum, r) => sum + (r.client_count || r.customers || 0), 0);

  // حساب نسبة النمو/التراجع
  const computeDelta = (current, prev) => {
    if (!prev || prev === 0) return { pct: null, direction: 'neutral' };
    const pct = ((current - prev) / prev) * 100;
    return {
      pct: Math.abs(pct),
      direction: pct > 0 ? 'up' : pct < 0 ? 'down' : 'neutral',
      signed: pct,
    };
  };
  const revDelta = computeDelta(totalRevenue, yoyStats.prevRevenue);
  const cliDelta = computeDelta(totalClients, yoyStats.prevClients);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t('revenues')}</h1>
          <p className="text-muted-foreground">إدارة الإيرادات اليومية</p>
        </div>
        <div className="flex gap-3">
          <DataManager
            collectionName="revenues"
            collectionLabel="الإيرادات"
            items={revenues}
            onRefresh={fetchData}
            idField="id"
            nameField="description"
            isAdmin={isAdmin()}
          />
          
          {/* زر طباعة التقرير */}
          <Button 
            onClick={() => setShowPrintModal(true)} 
            variant="outline" 
            className="border-primary/50 text-primary hover:bg-primary/10"
          >
            <Printer className="w-4 h-4 me-2" />
            طباعة تقرير
          </Button>
          
          {/* زر تحميل PDF */}
          <Button 
            onClick={downloadPDFReport} 
            variant="outline" 
            className="border-green-500/50 text-green-500 hover:bg-green-500/10"
          >
            <FileDown className="w-4 h-4 me-2" />
            تحميل PDF
          </Button>

          {/* زر تصدير CSV */}
          <Button
            onClick={exportCSV}
            variant="outline"
            className="border-blue-500/50 text-blue-500 hover:bg-blue-500/10"
            data-testid="revenues-export-csv-btn"
          >
            <FileDown className="w-4 h-4 me-2" />
            تصدير CSV
          </Button>
          
          {canAddOrEdit && (
            <Dialog open={open} onOpenChange={(open) => {
              setOpen(open);
              if (open && !editingRevenue) {
                // Update time and date when dialog opens for new revenue
                setFormData(prev => ({
                  ...prev,
                  date: getUAEDateString(),
                  time: getUAETimeString()
                }));
              }
              if (!open) {
                setEditingRevenue(null);
                setFormData({
                  barber_id: '',
                amount: '',
                client_count: '',
                date: getUAEDateString(),
                time: getUAETimeString(),
                description: ''
              });
            }
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-revenue-btn" className="bg-primary text-primary-foreground">
                <Plus className="w-4 h-4 me-2" />
                {t('addRevenue')}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-white/10">
              <DialogHeader>
                <DialogTitle>{editingRevenue ? t('edit') : t('addRevenue')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* إظهار حقل الحلاق فقط للمدير */}
                {isAdmin() ? (
                  <div>
                    <label className="block text-sm font-medium mb-2">الحلاق</label>
                    <Select 
                      value={formData.barber_id} 
                      onValueChange={(value) => setFormData({ ...formData, barber_id: value })}
                    >
                      <SelectTrigger data-testid="revenue-barber-select" className="bg-secondary/50 border-white/10">
                        <SelectValue placeholder="اختر الحلاق" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-white/10">
                        {barbers.map(barber => (
                          <SelectItem key={barber.id} value={barber.id}>
                            {barber.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  /* للحلاق: عرض اسمه فقط */
                  <div className="bg-primary/10 border border-primary/20 rounded-lg p-3">
                    <p className="text-sm text-muted-foreground">الحلاق:</p>
                    <p className="font-bold text-primary">{currentBarber?.name || user?.name}</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium mb-2"><CurrencyLabel label={t('amount')} /></label>
                  <Input
                    data-testid="revenue-amount-input"
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    required
                    className="bg-secondary/50 border-white/10"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">عدد الزبائن</label>
                  <Input
                    data-testid="revenue-clients-input"
                    type="number"
                    value={formData.client_count}
                    onChange={(e) => setFormData({ ...formData, client_count: e.target.value })}
                    required
                    className="bg-secondary/50 border-white/10"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{t('date')}</label>
                  <Input
                    data-testid="revenue-date-input"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    className="bg-secondary/50 border-white/10"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">الوقت</label>
                  <Input
                    data-testid="revenue-time-input"
                    type="time"
                    value={formData.time}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                    required
                    className="bg-secondary/50 border-white/10"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">{t('description')}</label>
                  <Input
                    data-testid="revenue-description-input"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="bg-secondary/50 border-white/10"
                  />
                </div>
                {isBarber() && (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-500" />
                    <span className="text-sm text-amber-500">يمكنك التعديل خلال ساعة واحدة فقط من وقت الإضافة. الحذف متاح للمدير فقط.</span>
                  </div>
                )}
                <Button data-testid="submit-revenue-btn" type="submit" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground">
                  {isSubmitting ? '⏳ جاري الحفظ...' : t('save')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6 md:col-span-1">
          <p className="text-sm text-muted-foreground mb-2">الشهر</p>
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger data-testid="revenues-month-select" className="bg-secondary/50 border-white/10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-card border-white/10 max-h-72">
              {monthOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} data-testid={`revenues-month-opt-${opt.value}`}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-muted-foreground mt-2">يعرض إيرادات حتى 12 شهرًا</p>
        </div>
        <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6">
          <p className="text-sm text-muted-foreground mb-2">{t('totalRevenue')} (شهر {monthOptions.find(o => o.value === selectedMonth)?.label})</p>
          <p className="text-3xl font-bold text-primary" data-testid="revenues-month-total"><CurrencyDisplay amount={totalRevenue} className="text-primary" /></p>
          <div data-testid="revenues-yoy-revenue">
            <YoYBadge delta={revDelta} prevValue={yoyStats.prevRevenue} lastYearMonth={yoyStats.lastYearMonth} isCurrency />
          </div>
        </div>
        <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6">
          <p className="text-sm text-muted-foreground mb-2">إجمالي الزبائن (الشهر)</p>
          <p className="text-3xl font-bold text-blue-500" data-testid="revenues-month-clients">{totalClients}</p>
          <div data-testid="revenues-yoy-clients">
            <YoYBadge delta={cliDelta} prevValue={yoyStats.prevClients} lastYearMonth={yoyStats.lastYearMonth} />
          </div>
        </div>
      </div>

      {/* أفضل الأداء في الشهر: أفضل يوم بالتاريخ — أفضل يوم في الأسبوع — أفضل أسبوع */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <PeakCard
          icon={Trophy}
          accent="amber"
          title="أفضل يوم في الشهر"
          empty="لا توجد بيانات"
          primary={peakStats.bestDay?.label}
          amount={peakStats.bestDay?.amount}
          clients={peakStats.bestDay?.clients}
          testId="peak-best-day"
          onClick={peakStats.bestDay ? () => {
            const v = peakStats.bestDay.date;
            setDrillFilter((cur) => (cur?.type === 'day' && cur.value === v) ? null : { type: 'day', value: v });
          } : undefined}
          isActive={drillFilter?.type === 'day' && drillFilter.value === peakStats.bestDay?.date}
        />
        <PeakCard
          icon={CalendarDays}
          accent="emerald"
          title="أفضل يوم في الأسبوع"
          empty="لا توجد بيانات"
          primary={peakStats.bestWeekday?.name}
          subtitle={peakStats.bestWeekday ? `مكرر ${peakStats.bestWeekday.daysCount} ${peakStats.bestWeekday.daysCount === 1 ? 'مرة' : 'مرات'} هذا الشهر` : null}
          amount={peakStats.bestWeekday?.amount}
          clients={peakStats.bestWeekday?.clients}
          testId="peak-best-weekday"
          onClick={peakStats.bestWeekday ? () => {
            const v = peakStats.bestWeekday.weekdayIdx;
            setDrillFilter((cur) => (cur?.type === 'weekday' && cur.value === v) ? null : { type: 'weekday', value: v });
          } : undefined}
          isActive={drillFilter?.type === 'weekday' && drillFilter.value === peakStats.bestWeekday?.weekdayIdx}
        />
        <PeakCard
          icon={CalendarRange}
          accent="blue"
          title="أفضل أسبوع في الشهر"
          empty="لا توجد بيانات"
          primary={peakStats.bestWeek ? `الأسبوع ${peakStats.bestWeek.number}` : null}
          subtitle={peakStats.bestWeek ? `${peakStats.bestWeek.daysCount} ${peakStats.bestWeek.daysCount === 1 ? 'يوم' : 'أيام'} نشطة` : null}
          amount={peakStats.bestWeek?.amount}
          clients={peakStats.bestWeek?.clients}
          testId="peak-best-week"
          onClick={peakStats.bestWeek ? () => {
            const v = peakStats.bestWeek.number;
            setDrillFilter((cur) => (cur?.type === 'week' && cur.value === v) ? null : { type: 'week', value: v });
          } : undefined}
          isActive={drillFilter?.type === 'week' && drillFilter.value === peakStats.bestWeek?.number}
        />
      </div>

      {/* شريط فلتر الـ drill النشط */}
      {drillFilter && (
        <div className="flex items-center justify-between bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-2" data-testid="drill-filter-bar">
          <div className="text-sm text-amber-400 font-semibold flex items-center gap-2">
            <span>الجدول مفلتر على:</span>
            <span className="bg-amber-500/20 px-2 py-0.5 rounded-md text-amber-300" data-testid="drill-filter-label">
              {drillFilter.type === 'day' && peakStats.bestDay?.label}
              {drillFilter.type === 'weekday' && `كل أيام ${WEEKDAYS_AR[drillFilter.value]}`}
              {drillFilter.type === 'week' && `الأسبوع ${drillFilter.value} من الشهر`}
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDrillFilter(null)}
            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
            data-testid="drill-filter-clear"
          >
            ✕ إلغاء الفلتر
          </Button>
        </div>
      )}

      <div className="bg-card border border-white/5 rounded-xl shadow-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary/50 border-b border-white/5">
              <tr>
                <th className="px-6 py-4 text-start text-sm font-semibold">{t('date')}</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">الوقت</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">الحلاق</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">{t('amount')}</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">الزبائن</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">{t('description')}</th>
                <th className="px-6 py-4 text-start text-sm font-semibold">{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredRevenues.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-sm text-muted-foreground" data-testid="revenues-empty-row">
                    لا توجد إيرادات في هذا الشهر
                  </td>
                </tr>
              )}
              {filteredRevenues.map((revenue) => {
                const barber = barbers.find(b => b.id === revenue.barber_id);
                const canEdit = isAdmin() || canBarberEdit(revenue);
                const timeRemaining = isBarber() && currentBarber?.id === revenue.barber_id ? getTimeRemaining(revenue) : null;
                const isOwnRevenue = currentBarber?.id === revenue.barber_id;
                
                return (
                  <tr key={revenue.id} className="border-b border-white/5 hover:bg-secondary/20">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium">{getUAEDayName(revenue.date)}</div>
                        <div className="text-xs text-muted-foreground">{formatUAEDateDDMMYYYY(revenue.date)}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">{revenue.receipt_time || getUAETimeHHMM(revenue.date) || '-'}</td>
                    <td className="px-6 py-4">{barber?.name || 'N/A'}</td>
                    <td className="px-6 py-4 text-primary font-semibold"><CurrencyDisplay amount={revenue.amount} className="text-primary" /></td>
                    <td className="px-6 py-4">{revenue.client_count || revenue.customers || 0}</td>
                    <td className="px-6 py-4 text-muted-foreground">{revenue.description || '-'}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {/* زر التعديل */}
                        {canEdit && (
                          <Button
                            data-testid={`edit-revenue-${revenue.id}`}
                            onClick={() => handleEdit(revenue)}
                            variant="ghost"
                            size="sm"
                            className="text-primary hover:bg-primary/10"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {/* زر الحذف - للمدير فقط */}
                        {isAdmin() && (
                          <Button
                            data-testid={`delete-revenue-${revenue.id}`}
                            onClick={() => handleDelete(revenue.id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {/* للحلاق - إظهار الوقت المتبقي أو رسالة انتهاء الفترة */}
                        {isBarber() && isOwnRevenue && (
                          timeRemaining ? (
                            <span className="text-xs text-amber-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {timeRemaining}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Lock className="w-3 h-3" />
                              انتهت فترة التعديل
                            </span>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* نافذة طباعة التقرير */}
      <PrintReportModal
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        reportType="revenues"
        barbers={barbers}
        currentBarber={currentBarber}
        isAdmin={isAdmin()}
      />
    </div>
  );
};

export default Revenues;
